import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trpc } from "../../../lib/trpc";
import { Icon } from "../../ui/Icon";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../../ui/Toast";
import { SearchSelect } from "../../ui/SearchSelect";
import { Drawer } from "../../Drawer";
import { useIsMobile } from "../../../lib/useIsMobile";
import { MCard } from "../../../components/mobile/primitives";
import "../../../pages/Items.css";
import "../../../pages/Maintenance.css"; // mt-empty (liste "Yükleniyor…")

/**
 * İnsan Kaynakları — Puantaj (Devam Takibi) sekmesi.
 *
 * Üstte personel (SearchSelect) + ay seçici; seçilen personel-ay için aylık özet KPI'ları
 * (present/absent/leave/toplam saat/mesai) ve gün-bazlı puantaj tablosu. Yazma yetkisi (canManage)
 * varsa satıra tıklayınca / "Gün Ekle" ile küçük form (Drawer) açılır → upsert; silme onaylı.
 * Veri: trpc.hr.attendance.{list,upsert,remove,monthlySummary} + trpc.hr.employee.list.
 */

type AttStatus = "present" | "absent" | "leave" | "holiday" | "weekend" | "half_day" | "remote";

/** Durum = renk + ikon + metin (STATUS_META Record kalıbı). */
const STATUS_META: Record<AttStatus, { cls: string; label: string; icon: string }> = {
  present: { cls: "b-success", label: "İşte", icon: "check" },
  absent: { cls: "b-danger", label: "Devamsız", icon: "x" },
  leave: { cls: "b-info", label: "İzinli", icon: "calendar" },
  holiday: { cls: "b-primary", label: "Resmi Tatil", icon: "star" },
  weekend: { cls: "", label: "Hafta Sonu", icon: "minus" },
  half_day: { cls: "b-warn", label: "Yarım Gün", icon: "clock" },
  remote: { cls: "b-info", label: "Uzaktan", icon: "home" },
};
const STATUS_KEYS = Object.keys(STATUS_META) as AttStatus[];

interface DayForm {
  mode: "new" | "edit";
  attId: string | null;
  date: string; // YYYY-MM-DD
  status: AttStatus;
  checkIn: string; // "HH:MM" | ""
  checkOut: string;
  hoursWorked: string; // sayısal metin | ""
  overtimeHours: string;
  /** Saatlik devamsızlık — motor DEĞİL insan yazar; bordroda gün karşılığı kadar kesilir. */
  absenceHours: string;
  note: string;
}

const pad = (n: number) => String(n).padStart(2, "0");
function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}
function weekdayShort(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("tr-TR", { weekday: "short" });
}
function fmtNum(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

export function AttendanceTab({ canManage, isSuper }: { canManage: boolean; isSuper: boolean }) {
  void isSuper; // Puantaj'da maaş bölümü yok — imza sözleşmesi gereği alınır.
  const qc = trpc.useUtils();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [ym, setYm] = useState<string>(() => {
    const n = new Date();
    return `${n.getFullYear()}-${pad(n.getMonth() + 1)}`;
  });
  const [editState, setEditState] = useState<DayForm | null>(null);
  const [delOpen, setDelOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) =>
    setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  // ── Ay çözümü (geçersiz/temizlenmiş ay → hint) ──
  const now = new Date();
  const defaultYm = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const todayStr = `${defaultYm}-${pad(now.getDate())}`;
  const monthValid = /^\d{4}-\d{2}$/.test(ym) && Number(ym.slice(5, 7)) >= 1 && Number(ym.slice(5, 7)) <= 12;
  const ymSafe = monthValid ? ym : defaultYm;
  const year = Number(ymSafe.slice(0, 4));
  const month = Number(ymSafe.slice(5, 7));
  const from = `${ymSafe}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to = `${ymSafe}-${pad(lastDay)}`;

  // ── Sorgular ──
  const employeesQ = trpc.hr.employee.list.useQuery(undefined, { staleTime: 60_000 });
  const enabled = !!employeeId && monthValid;
  const listQ = trpc.hr.attendance.list.useQuery(
    { employeeId: employeeId ?? "", from, to },
    { enabled, staleTime: 5_000 }
  );
  const summaryQ = trpc.hr.attendance.monthlySummary.useQuery(
    { employeeId: employeeId ?? "", year, month },
    { enabled, staleTime: 5_000 }
  );
  // Ay durumu personelden BAĞIMSIZ (tesis-ay kapsamı) → personel seçilmeden de gösterilir.
  const statusQ = trpc.hr.attendance.monthStatus.useQuery(
    { year, month },
    { enabled: monthValid, staleTime: 5_000 }
  );

  const rows = listQ.data ?? [];
  // useMemo ŞART: her render'da yeni dizi, SearchSelect'in iç memo'sunu tüm personel listesi
  // için geçersiz kılıyordu (kardeş sekmeler zaten memoize ediyor).
  const empOptions = useMemo(
    () => (employeesQ.data ?? []).map((e) => ({
      value: e.id,
      label: e.fullName,
      desc: e.positionTitle ?? undefined,
      tail: e.code ?? undefined,
    })),
    [employeesQ.data],
  );

  // ── Mutasyonlar ──
  const invalidate = () => qc.hr.attendance.invalidate();
  const upsertMut = trpc.hr.attendance.upsert.useMutation({
    onSuccess: () => {
      invalidate();
      setEditState(null);
      pushToast({ tone: "success", message: "Puantaj kaydedildi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message }),
  });
  const calcMut = trpc.hr.attendance.calculateMonth.useMutation({
    onSuccess: (r) => {
      invalidate();
      setCalcOpen(false);
      const parts = [
        `${r.created} yeni`,
        `${r.updated} güncellendi`,
        `${r.unchanged} değişmedi`,
        ...(r.deleted ? [`${r.deleted} bayat silindi`] : []),
        ...(r.skippedManual ? [`${r.skippedManual} elle giriş korundu`] : []),
      ];
      // 200 personellik tesiste uyarı sayısı 200'ü bulabilir; toast'ın kendi kaydırması yok
      // ve ekranı taşarsa alttakiler OKUNAMAZ. İlk N gösterilir, gerisi sayıyla belirtilir.
      const WMAX = 8;
      const wLines = r.warnings.map((w) => (w.days && w.days > 1 ? `${w.message} (${w.days} gün)` : w.message));
      pushToast({
        tone: r.warnings.length ? "warning" : "success",
        title: `${r.employeeCount} personel · ${r.dayCount} gün hesaplandı`,
        message: parts.join(" · "),
        // Uyarılar okunmadan kaybolmasın: varsa otomatik kapanma KAPALI.
        duration: r.warnings.length ? 0 : undefined,
        lines: wLines.length > WMAX
          ? [...wLines.slice(0, WMAX), `… ve ${wLines.length - WMAX} uyarı daha`]
          : wLines,
      });
    },
    onError: (e) => { setCalcOpen(false); pushToast({ tone: "danger", title: "Hesaplanamadı", message: e.message }); },
  });
  const removeMut = trpc.hr.attendance.remove.useMutation({
    onSuccess: () => {
      invalidate();
      setDelOpen(false);
      setEditState(null);
      pushToast({ tone: "success", message: "Puantaj kaydı silindi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message }),
  });

  const upd = (patch: Partial<DayForm>) => setEditState((s) => (s ? { ...s, ...patch } : s));

  /**
   * TOPLU EKRANLAR ARTIK SEKME.
   *
   * Çekmece yerine uygulama içi sekmeye gidilir; seçili ay `?d=YYYY-MM` ile taşınır ki
   * kullanıcı ekranda 08/2026 görürken kayıt başka aya düşmesin. Sekme zaten açıksa
   * TabBar onu öne getirir ve adresteki ay yeni değerle senkronlanır (bkz. useBulkPeriod).
   */
  const openBulk = (path: string) => navigate(`${path}?d=${ymSafe}`);

  function openEdit(r: (typeof rows)[number]) {
    if (!canManage) return;
    setDelOpen(false);
    setEditState({
      mode: "edit",
      attId: r.id,
      date: r.date,
      status: r.status as AttStatus,
      checkIn: r.checkIn ?? "",
      checkOut: r.checkOut ?? "",
      hoursWorked: r.hoursWorked == null ? "" : String(r.hoursWorked),
      overtimeHours: r.overtimeHours == null ? "" : String(r.overtimeHours),
      absenceHours: r.absenceHours == null ? "" : String(r.absenceHours),
      note: r.note ?? "",
    });
  }
  function openNew() {
    if (!enabled) return;
    setDelOpen(false);
    const gun = todayStr >= from && todayStr <= to ? todayStr : from;
    /**
     * SEÇİLEN GÜNÜN KAYDI VARSA ONU DÜZENLE.
     *
     * "Gün Ekle" boş bir form açıyor, tarih ise ay içinde SERBEST; sunucu `upsert` ise
     * satır var mı diye bakmıyor. Ay "Puantaj Hesapla" ile üretilmişse HER günün satırı
     * vardır: kullanıcı yalnız not yazıp kaydettiğinde o günün mesaisi ve saatlik
     * devamsızlığı SESSİZCE siliniyordu (kısmi upsert artık dokunmuyor, ama form o
     * alanları BOŞ olarak GÖNDERİYOR — yani açıkça null demiş oluyor).
     * Çözüm: gün doluysa form mevcut değerlerle açılır ve düzenleme moduna geçer.
     */
    const mevcut = rows.find((x) => x.date === gun);
    if (mevcut) { openEdit(mevcut); return; }
    setEditState({
      mode: "new",
      attId: null,
      date: gun,
      status: "present",
      checkIn: "",
      checkOut: "",
      hoursWorked: "",
      overtimeHours: "",
      absenceHours: "",
      note: "",
    });
  }

  /**
   * "Yeni" modda tarih DEĞİŞTİRİLİNCE de aynı kontrol: kullanıcı 10 Eylül'ü seçtiğinde
   * o günün kaydı varsa form onun değerleriyle dolar. Aksi hâlde boş form o günün
   * mesai/devamsızlık verisini sıfırlayan bir kayıt gönderirdi.
   */
  function pickNewDate(iso: string) {
    const mevcut = rows.find((x) => x.date === iso);
    if (mevcut) { openEdit(mevcut); return; }
    upd({ date: iso });
  }

  const numOk = (s: string) => {
    if (s.trim() === "") return true;
    const n = Number(s);
    return !isNaN(n) && n >= 0;
  };
  const formValid =
    !!editState &&
    (editState.mode === "edit" || !!editState.date) &&
    numOk(editState.hoursWorked) &&
    numOk(editState.overtimeHours) &&
    numOk(editState.absenceHours);

  function submitDay() {
    if (!editState || !employeeId) return;
    if (editState.mode === "new" && !editState.date) return;
    if (!numOk(editState.hoursWorked) || !numOk(editState.overtimeHours) || !numOk(editState.absenceHours)) {
      pushToast({ tone: "danger", message: "Saat değerleri 0 veya daha büyük olmalı." });
      return;
    }
    upsertMut.mutate({
      employeeId,
      date: editState.date,
      status: editState.status,
      checkIn: editState.checkIn.trim() === "" ? null : editState.checkIn,
      checkOut: editState.checkOut.trim() === "" ? null : editState.checkOut,
      hoursWorked: editState.hoursWorked.trim() === "" ? null : Number(editState.hoursWorked),
      overtimeHours: editState.overtimeHours.trim() === "" ? null : Number(editState.overtimeHours),
      // Çalışılmayan güne geçildiyse saat GÖNDERİLMEZ (null = temizle) — aksi hâlde sunucu
      // haklı olarak reddeder ve kullanıcı durumu değiştiremez hâle gelirdi.
      absenceHours: (editState.status !== "present" && editState.status !== "remote")
        ? null
        : (editState.absenceHours.trim() === "" ? null : Number(editState.absenceHours)),
      note: editState.note.trim() === "" ? null : editState.note.trim(),
    });
  }

  const kpi = (label: string, val: string | number, sub: string, color: string, unit?: string) => (
    <div className="dash-kpi" style={{ ["--kpi-color" as string]: color }}>
      <div className="head">
        <span className="lbl">{label}</span>
      </div>
      <div className="val">
        {val}
        {unit ? <span className="unit">{unit}</span> : null}
      </div>
      <div className="sub">{sub}</div>
    </div>
  );

  // ── Ay durumu şeridi: "ne zaman hesaplandı, o günden beri ne değişti" ──
  const st = statusQ.data;
  // `statusQ` yuklenirken `locked` false olur ve buton bir an ACIK kalirdi (sunucu guard'i
  // yakalar ama kullanici kilitli aya basip hata gorur). Yuklenirken de kapali tut.
  const locked = statusQ.isLoading || st?.payrollStatus === "approved" || st?.payrollStatus === "paid";
  const stale = !!st?.staleSince;
  const fmtDT = (d: Date | string | null | undefined) =>
    d ? new Date(d).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  // ── Aksiyon overlay'leri (Drawer + onay + toast) — hem masaüstü hem mobil return'de {drawers} olarak render edilir ──
  const drawers = (
    <>
      {/* ── Gün ekle / düzenle formu (Drawer) ── */}
      <Drawer
        open={!!editState}
        onClose={() => setEditState(null)}
        size="md"
        dismissible={false}
        title={editState ? (editState.mode === "edit" ? `Puantaj · ${fmtDate(editState.date)}` : "Gün Ekle") : ""}
        subtitle={
          editState
            ? `${empOptions.find((o) => o.value === employeeId)?.label ?? "Personel"} · ${monthValid ? fmtDate(from).slice(3) : ""}`
            : undefined
        }
        footer={
          editState ? (
            <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
              <button
                type="button"
                className="btn-md primary"
                onClick={submitDay}
                disabled={!formValid || upsertMut.isPending}
              >
                {upsertMut.isPending ? "Kaydediliyor…" : "Kaydet"}
              </button>
              {editState.mode === "edit" ? (
                <button type="button" className="btn-md danger-outline" onClick={() => setDelOpen(true)}>
                  <Icon name="trash" size={14} />
                  Sil
                </button>
              ) : null}
              <div style={{ flex: 1 }} />
              <button type="button" className="btn-md ghost" onClick={() => setEditState(null)}>
                Vazgeç
              </button>
            </div>
          ) : null
        }
      >
        {editState ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="form-field">
              <label className="lbl" htmlFor="att-date">
                Tarih <span className="req">*</span>
              </label>
              <input
                id="att-date"
                className="form-input"
                type="date"
                value={editState.date}
                min={from}
                max={to}
                disabled={editState.mode === "edit"}
                onChange={(e) => pickNewDate(e.target.value)}
              />
              {editState.mode === "edit" ? (
                <span className="help">Tarih kaydın anahtarıdır; değiştirmek için silip yeniden ekleyin.</span>
              ) : null}
            </div>

            <div className="form-field">
              <label className="lbl" htmlFor="att-status">Durum</label>
              <select
                id="att-status"
                className="form-select"
                value={editState.status}
                onChange={(e) => upd({ status: e.target.value as AttStatus })}
              >
                {STATUS_KEYS.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s].label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="att-in">Giriş</label>
                <input
                  id="att-in"
                  className="form-input"
                  type="time"
                  value={editState.checkIn}
                  onChange={(e) => upd({ checkIn: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="att-out">Çıkış</label>
                <input
                  id="att-out"
                  className="form-input"
                  type="time"
                  value={editState.checkOut}
                  onChange={(e) => upd({ checkOut: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="lbl" htmlFor="att-hw">Çalışılan Saat</label>
                <input
                  id="att-hw"
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.25"
                  value={editState.hoursWorked}
                  onChange={(e) => upd({ hoursWorked: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="att-ot">Mesai (Saat)</label>
                <input
                  id="att-ot"
                  className="form-input"
                  type="number"
                  min="0"
                  step="0.25"
                  value={editState.overtimeHours}
                  onChange={(e) => upd({ overtimeHours: e.target.value })}
                  placeholder="0"
                />
              </div>
              {/* SAATLİK DEVAMSIZLIK: "geldi ama 3 saat yoktu" durumu. `status` motorun
                  alanı olduğu için oraya yazılamaz — yeniden hesapta silinirdi.
                  ÇALIŞILMAYAN GÜNDE KAPALI: sunucu da reddediyor (aynı gün hem statüden
                  hem saatten kesilirse ÇİFT KESİNTİ olur); alanı açık bırakmak kullanıcıya
                  yapamayacağı bir şeyi vaat etmek olurdu. */}
              {(() => {
                const calisilanGun = editState.status === "present" || editState.status === "remote";
                const gunSaati = editState.hoursWorked.trim() === "" ? null : Number(editState.hoursWorked);
                return (
                  <div className="form-field">
                    <label className="lbl" htmlFor="att-abs">Devamsızlık (Saat)</label>
                    <input
                      id="att-abs"
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.25"
                      max={gunSaati ?? undefined}
                      disabled={!calisilanGun}
                      value={calisilanGun ? editState.absenceHours : ""}
                      onChange={(e) => upd({ absenceHours: e.target.value })}
                      placeholder="0"
                      aria-describedby="att-abs-help"
                    />
                    <span className="help" id="att-abs-help">
                      {!calisilanGun
                        ? `Durum "${STATUS_META[editState.status].label}" — saatlik devamsızlık yalnız çalışılan güne girilir. Tam gün için durumu "Devamsız" yapın, saat girmeyin.`
                        : gunSaati == null || gunSaati <= 0
                          ? "Önce \"Çalışılan Saat\" alanını doldurun — gün karşılığı onun oranıyla hesaplanır."
                          : `Bordroda gün karşılığı kadar kesilir (saat ÷ ${fmtNum(gunSaati)} sa). En çok ${fmtNum(gunSaati)} sa.`}
                    </span>
                  </div>
                );
              })()}
            </div>

            <div className="form-field">
              <label className="lbl" htmlFor="att-note">Not</label>
              <textarea
                id="att-note"
                className="form-textarea"
                rows={2}
                value={editState.note}
                onChange={(e) => upd({ note: e.target.value })}
                placeholder="İsteğe bağlı"
              />
              <span className="help">Giriş saati verilir de vardiya seçilmezse otomatik çözülür.</span>
            </div>
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={calcOpen}
        title={`${ymSafe.slice(5)}/${ymSafe.slice(0, 4)} puantajı hesaplansın mı?`}
        message={
          <>
            Bu tesisteki <b>tüm personelin</b> {ymSafe.slice(5)}/{ymSafe.slice(0, 4)} ayı, vardiya planı ve
            onaylı izinlerden yeniden üretilir.
            {st?.manualCount ? <> <b>Elle girdiğiniz {st.manualCount} gün korunur</b> — motor onlara dokunmaz.</> : null}
            {" "}Kişi devamsızlık veya mesai girilmediği sürece vardiyasına göre <b>devamlı</b> sayılır.
          </>
        }
        confirmLabel="Hesapla"
        cancelLabel="Vazgeç"
        busy={calcMut.isPending}
        onCancel={() => setCalcOpen(false)}
        onConfirm={() => calcMut.mutate({ year, month })}
      />

      <ConfirmDialog
        open={delOpen}
        title="Puantaj kaydını sil"
        message={editState ? `"${fmtDate(editState.date)}" günü silinecek. Bu işlem geri alınamaz.` : ""}
        confirmLabel="Sil"
        tone="danger"
        busy={removeMut.isPending}
        onCancel={() => setDelOpen(false)}
        onConfirm={() => editState?.attId && removeMut.mutate({ id: editState.attId })}
      />

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );

  // ── Aylık özet KPI'ları (masaüstü + mobil ortak) ──
  const s = summaryQ.data;
  const summaryKpis = (
    <>
      {/* workedDays = present + remote + yarım günün yarısı — bordronun kullandığı sayı. */}
      {kpi("ÇALIŞMA GÜNÜ", s ? fmtNum(s.workedDays) : "…", "ücrete esas", "var(--success)")}
      {kpi("DEVAMSIZ", s ? fmtNum(s.absentDays) : "…", "gün", "var(--danger)")}
      {kpi("İZİNLİ", s ? s.leave : "…",
        s ? `${s.paidLeaveDays} ücretli · ${s.unpaidLeaveDays} ücretsiz${s.unknownLeaveDays ? ` · ${s.unknownLeaveDays} belirsiz` : ""}` : "gün",
        "var(--info)")}
      {kpi("TOPLAM SAAT", s ? fmtNum(s.totalHours) : "…", "çalışma", "var(--primary)", "sa")}
      {kpi("MESAİ", s ? fmtNum(s.overtime) : "…", "fazla mesai", "var(--warning)", "sa")}
      {/* Saatlik devamsızlık AYRI gösterilir: "DEVAMSIZ" KPI'ı gün cinsindendir ve bu
          saatlerin gün karşılığını zaten İÇERİR — ham saat görünmezse kullanıcı
          0,4 günün nereden geldiğini bulamaz. */}
      {kpi("DEVAMSIZLIK", s ? fmtNum(s.absenceHours) : "…",
        s && s.absenceDaysFromHours > 0 ? `≈ ${fmtNum(s.absenceDaysFromHours)} gün (devamsıza dahil)` : "saatlik",
        "var(--danger)", "sa")}
    </>
  );

  const statusStrip = st ? (
    <div className={`att-status${stale ? " stale" : ""}${locked ? " locked" : ""}`} role="status" aria-live="polite">
      <Icon name={locked ? "lock" : stale ? "alertTriangle" : st.lastCalculatedAt ? "checkCircle" : "info"} size={14} />
      <span className="att-status-txt">
        {locked ? (
          <><b>Dönem kilitli</b> — {st.payrollCode ?? "bordro"} {st.payrollStatus === "paid" ? "ödendi" : "onaylandı"}; puantaj değiştirilemez.</>
        ) : !st.lastCalculatedAt ? (
          <><b>Bu ay henüz hesaplanmadı.</b> "Puantaj Hesapla" ile vardiya planı ve izinlerden otomatik üretin.</>
        ) : stale ? (
          <><b>Puantaj bayat</b> — son hesap {fmtDT(st.lastCalculatedAt)}; o gün<b>den</b> sonra {st.reasons.join(" ve ")} değişti. Yeniden hesaplayın.</>
        ) : (
          <>Son hesap <b>{fmtDT(st.lastCalculatedAt)}</b> — güncel.</>
        )}
      </span>
      <span className="grow" />
      <span className="att-status-n">
        <b className="mono">{st.autoCount}</b> otomatik
        {st.manualCount ? <> · <b className="mono">{st.manualCount}</b> elle</> : null}
      </span>
    </div>
  ) : null;

  const calcBtn = canManage ? (
    <button
      type="button"
      className={`btn-md ${stale || !st?.lastCalculatedAt ? "primary" : "secondary"}`}
      disabled={!monthValid || locked || calcMut.isPending}
      onClick={() => setCalcOpen(true)}
      title={locked ? "Dönem bordrosu onaylandığı için puantaj kilitli" : "Vardiya planı ve izinlerden tüm personelin ayını hesapla"}
    >
      <Icon name="sigma" size={14} />{calcMut.isPending ? "Hesaplanıyor…" : "Puantaj Hesapla"}
    </button>
  ) : null;

  if (isMobile) {
    const mb = (c: string) => (c === "b-warn" ? "b-warning" : c || "b-neutral");
    return (
      <>
        <div className="m-stickytop">
          {canManage ? (
            <div className="m-actions">
              <button
                type="button"
                className={`btn-md ${stale || !st?.lastCalculatedAt ? "primary" : "secondary"} sm`}
                disabled={!monthValid || locked || calcMut.isPending}
                title={locked ? "Dönem bordrosu onaylandığı için puantaj kilitli" : "Vardiya planı ve izinlerden tüm personelin ayını hesapla"}
                onClick={() => setCalcOpen(true)}
              >
                <Icon name="sigma" size={14} />{calcMut.isPending ? "Hesaplanıyor…" : "Puantaj Hesapla"}
              </button>
              <button
                type="button"
                className="btn-md secondary sm"
                disabled={!monthValid || locked}
                onClick={() => openBulk("/personel/mesai")}
                title={locked ? "Dönem bordrosu onaylandığı için puantaj kilitli" : "Çok personele tek seferde fazla mesai gir"}
              >
                <Icon name="clock" size={14} />Mesai
              </button>
              <button
                type="button"
                className="btn-md secondary sm"
                disabled={!monthValid || locked}
                onClick={() => openBulk("/personel/devamsizlik")}
                title={locked ? "Dönem bordrosu onaylandığı için puantaj kilitli" : "Çok personele tek seferde devamsızlık gir"}
              >
                <Icon name="userX" size={14} />Devamsızlık
              </button>
              <button
                type="button"
                className="btn-md secondary sm"
                disabled={!enabled}
                onClick={openNew}
              >
                <Icon name="plus" size={14} />Gün Ekle
              </button>
            </div>
          ) : null}
          <label className="hr-mfld">
            <span className="m-ovl">Personel</span>
            <SearchSelect
              value={employeeId}
              options={empOptions}
              onChange={setEmployeeId}
              placeholder="Personel seç…"
              searchPlaceholder="Ad ya da kod ara…"
              emptyText={employeesQ.isLoading ? "Yükleniyor…" : "Personel bulunamadı"}
              icon="user"
            />
          </label>
          <label className="hr-mfld">
            <span className="m-ovl">Ay</span>
            <input className="form-input" type="month" value={ym} onChange={(e) => setYm(e.target.value)} />
          </label>
        </div>

        {statusStrip}

        {!employeeId ? (
          <div className="m-empty">Puantaj kayıtlarını görmek için bir personel ve ay seçin.</div>
        ) : !monthValid ? (
          <div className="m-empty">Puantaj özetini görmek için geçerli bir ay seçin.</div>
        ) : (
          <>
            <div className="dash-kpi-grid">{summaryKpis}</div>

            {listQ.isLoading ? (
              <div className="m-empty">Yükleniyor…</div>
            ) : listQ.isError ? (
              <div className="m-empty">{listQ.error.message}</div>
            ) : rows.length === 0 ? (
              <div className="m-empty">
                {canManage
                  ? '"Gün Ekle" ile bu personelin puantajını girmeye başlayın.'
                  : "Seçili ayda puantaj kaydı bulunmuyor."}
              </div>
            ) : (
              <>
                {rows.map((r) => {
                  const m = STATUS_META[r.status as AttStatus] ?? { cls: "", label: r.status, icon: "minus" };
                  const otHigh = r.overtimeHours != null && r.overtimeHours > 0;
                  return (
                    <MCard
                      pad
                      key={r.id}
                      onClick={canManage ? () => openEdit(r) : undefined}
                      style={canManage ? { cursor: "pointer" } : undefined}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: "var(--neutral-900)" }}>{fmtDate(r.date)}</span>
                          <span style={{ fontSize: 11, color: "var(--neutral-400)", marginLeft: 6 }}>{weekdayShort(r.date)}</span>
                        </span>
                        <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                        {r.source === "manual" ? (
                          <span className="badge b-warning no-dot" title="Elle girildi — yeniden hesapta korunur"><Icon name="edit" size={11} />Elle</span>
                        ) : null}
                      </div>
                      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div className="m-ovl">Giriş</div>
                          <div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.checkIn ?? "—"}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="m-ovl">Çıkış</div>
                          <div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.checkOut ?? "—"}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="m-ovl">Çalışılan</div>
                          <div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.hoursWorked == null ? "—" : `${fmtNum(r.hoursWorked)} sa`}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="m-ovl">Mesai</div>
                          <div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2, color: otHigh ? "var(--warning)" : undefined }}>{r.overtimeHours == null ? "—" : `${fmtNum(r.overtimeHours)} sa`}</div>
                        </div>
                      </div>
                      {r.note ? (
                        <div style={{ fontSize: 11.5, color: "var(--neutral-600)", marginTop: 8 }}>
                          <span style={{ color: "var(--neutral-400)" }}>Not </span>{r.note}
                        </div>
                      ) : null}
                    </MCard>
                  );
                })}
                <div className="hr-mcount">{rows.length} gün kayıtlı</div>
              </>
            )}
          </>
        )}

        {drawers}
      </>
    );
  }

  return (
    <div className="dt2-wrap">
      {/* Filtre çubuğu — Personeller sekmesiyle aynı desen (.dt2-toolbar + sabit hücreler). */}
      <div className="dt2-toolbar hr-toolbar">
        <div className="dt2-filter lg">
          <SearchSelect
            value={employeeId}
            options={empOptions}
            onChange={setEmployeeId}
            placeholder="Personel seç…"
            searchPlaceholder="Ad ya da kod ara…"
            emptyText={employeesQ.isLoading ? "Yükleniyor…" : "Personel bulunamadı"}
            icon="user"
          />
        </div>
        <div className="dt2-filter">
          <input
            id="att-month"
            className="form-input"
            type="month"
            value={ym}
            aria-label="Ay"
            onChange={(e) => setYm(e.target.value)}
          />
        </div>
        {employeeId || ym !== defaultYm ? (
          <button type="button" className="btn-md ghost" title="Filtreleri temizle"
            onClick={() => { setEmployeeId(null); setYm(defaultYm); }}>
            <Icon name="x" size={14} />Temizle
          </button>
        ) : null}
        <span className="grow" />
        {calcBtn}
        {canManage ? (
          <button
            type="button"
            className="btn-md secondary"
            disabled={!monthValid || locked}
            onClick={() => openBulk("/personel/mesai")}
            title={locked ? "Dönem bordrosu onaylandığı için puantaj kilitli" : "Çok personele tek seferde fazla mesai gir"}
          >
            <Icon name="clock" size={14} />Mesai
          </button>
        ) : null}
        {canManage ? (
          <button
            type="button"
            className="btn-md secondary"
            disabled={!monthValid || locked}
            onClick={() => openBulk("/personel/devamsizlik")}
            title={locked ? "Dönem bordrosu onaylandığı için puantaj kilitli" : "Çok personele tek seferde devamsızlık gir"}
          >
            <Icon name="userX" size={14} />Devamsızlık
          </button>
        ) : null}
        {canManage ? (
          <button
            type="button"
            className="btn-md secondary"
            disabled={!enabled}
            onClick={openNew}
            title={enabled ? "Gün ekle / işle" : "Önce personel ve ay seçin"}
          >
            <Icon name="plus" size={14} />Gün Ekle
          </button>
        ) : null}
      </div>

      {/* Durum şeridi personel seçiminden BAĞIMSIZ — hesaplama tesis-ay işlemidir. */}
      {statusStrip}

      {!employeeId ? (
        <div className="empty-state">
          <span className="ic" aria-hidden="true"><Icon name="users" size={30} /></span>
          <p className="ttl">Personel seçin</p>
          <p className="sub">Puantaj kayıtlarını görüntülemek ve düzenlemek için yukarıdan bir personel ve ay seçin.</p>
        </div>
      ) : !monthValid ? (
        <div className="empty-state">
          <span className="ic" aria-hidden="true"><Icon name="calendar" size={30} /></span>
          <p className="ttl">Ay seçin</p>
          <p className="sub">Puantaj özetini ve günlük kayıtları görmek için bir ay belirleyin.</p>
        </div>
      ) : (
        <>
          {/* KPI şeridi kartın İÇİNDE. Sabit 5 kolon inline'ı kaldırıldı: global media
              query'leri eziyor ve 768-1380px arası kart etiketlerini kırpıyordu. */}
          <div className="hr-kpi-band">
            <div className="dash-kpi-grid hr-kpi-auto">{summaryKpis}</div>
          </div>

          <div className="dt2-scroll">
            {listQ.isLoading ? (
              <div className="mt-empty">Yükleniyor…</div>
            ) : listQ.isError ? (
              <div className="form-banner-err hr-listerr">Puantaj yüklenemedi: {listQ.error.message}</div>
            ) : rows.length === 0 ? (
              <div className="empty-state">
                <span className="ic" aria-hidden="true"><Icon name="calendar" size={30} /></span>
                <p className="ttl">Bu ay için kayıt yok</p>
                <p className="sub">
                  {canManage
                    ? '"Gün Ekle" ile bu personelin puantajını girmeye başlayın.'
                    : "Seçili ayda puantaj kaydı bulunmuyor."}
                </p>
              </div>
            ) : (
              <table className="dt2">
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>Tarih</th>
                    <th style={{ width: 140 }}>Durum</th>
                    <th style={{ width: 96 }}>Kaynak</th>
                    <th style={{ width: 90 }}>Giriş</th>
                    <th style={{ width: 90 }}>Çıkış</th>
                    <th className="num" style={{ width: 120 }}>Çalışılan</th>
                    <th className="num" style={{ width: 110 }}>Mesai</th>
                    <th className="num" style={{ width: 120 }} title="Saatlik devamsızlık — bordroda gün karşılığı kadar kesilir">Devamsızlık</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const m = STATUS_META[r.status as AttStatus] ?? { cls: "", label: r.status, icon: "minus" };
                    const otHigh = r.overtimeHours != null && r.overtimeHours > 0;
                    return (
                      <tr
                        key={r.id}
                        className={canManage ? "hr-row" : undefined}
                        onClick={canManage ? () => openEdit(r) : undefined}
                        title={canManage ? "Düzenle" : undefined}
                      >
                        <td className="cell-id">
                          {fmtDate(r.date)}
                          <span className="sec">{weekdayShort(r.date)}</span>
                        </td>
                        <td>
                          <span className={`badge-pill ${m.cls}`}>
                            <Icon name={m.icon} size={11} />
                            {m.label}
                          </span>
                          {r.leaveTypeName ? <span className="att-leave">{r.leaveTypeName}{r.leavePaid === false ? " (ücretsiz)" : ""}</span> : null}
                        </td>
                        {/* Kaynak = bu satırın SAHİBİ. "elle" olan yeniden hesapta korunur. */}
                        <td>
                          <span className={`badge-pill ${r.source === "manual" ? "b-warn" : ""}`}
                            title={r.source === "manual" ? "Elle girildi — yeniden hesapta korunur" : "Motor üretti — yeniden hesapta tazelenir"}>
                            <Icon name={r.source === "manual" ? "edit" : "sigma"} size={11} />
                            {r.source === "manual" ? "Elle" : "Otomatik"}
                          </span>
                        </td>
                        <td className="mono hr-cell-sm">{r.checkIn ?? "—"}</td>
                        <td className="mono hr-cell-sm">{r.checkOut ?? "—"}</td>
                        <td className="cell-num">
                          {r.hoursWorked == null ? "—" : <>{fmtNum(r.hoursWorked)}<span className="unit">sa</span></>}
                        </td>
                        <td className={`cell-num${otHigh ? " hr-warn" : ""}`}>
                          {r.overtimeHours == null ? "—" : <>{fmtNum(r.overtimeHours)}<span className="unit">sa</span></>}
                        </td>
                        <td className="cell-num">
                          {r.absenceHours == null || r.absenceHours <= 0 ? "—" : (
                            <span
                              style={{ color: "var(--danger)", fontWeight: 600 }}
                              title={
                                r.hoursWorked && r.hoursWorked > 0
                                  ? `Gün karşılığı ≈ ${fmtNum(Math.min(1, r.absenceHours / r.hoursWorked))} gün`
                                  : "O günün çalışma saati yok — bordroda kesinti doğurmaz"
                              }
                            >
                              {fmtNum(r.absenceHours)}<span className="unit">sa</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="dt2-foot">
            <span>{listQ.isLoading ? "Yükleniyor…" : listQ.isError ? "—" : `${rows.length} gün kayıtlı`}</span>
          </div>
        </>
      )}

      {drawers}
    </div>
  );
}
